import noticeFr from './websitePrivacyNotice/fr';
import noticeEs from './websitePrivacyNotice/es';
import noticeAr from './websitePrivacyNotice/ar';

const CONTACT_ITEMS_EN = [
  'Demakstore Gıda İhracat San. ve Tic. Ltd. Şti.',
  'Operating Brand: DeMaxtore',
  'Website: www.demaxtore.com',
  'Email: support@demaxtore.com',
  'Alternative Email: demaxtore@demaxtore.com',
];

function dataSubsection(title, items) {
  return [
    { type: 'h3', text: title },
    { type: 'p', text: 'This may include:' },
    { type: 'ul', items },
  ];
}

const noticeEn = {
  title: 'Website Privacy Notice',
  lastUpdated: 'Last updated: 26 May 2026',
  back: '← Back to home',
  intro: [
    { type: 'h3', text: 'About This Privacy Notice' },
    { type: 'p', text: 'Your privacy is important to us.' },
    {
      type: 'p',
      text: 'This Website Privacy Notice explains how Demakstore Gıda İhracat San. ve Tic. Ltd. Şti., operating under the brand name DeMaxtore (“DeMaxtore”, “we”, “us”, or “our”), collects and processes personal data through your use of www.demaxtore.com.',
    },
    { type: 'p', text: 'This includes any personal data you may provide when you:' },
    {
      type: 'ul',
      items: [
        'Visit or browse our website',
        'Create or use an account',
        'Submit a sourcing request',
        'Use CommodityBid',
        'Use FreightIQ',
        'Request inspection services',
        'Request live shipment tracking support',
        'Contact us through forms, email, WhatsApp, phone, SMS, or other communication channels',
        'Present your company, products, or services through our platform',
        'Communicate with DeMaxtore as a buyer, supplier, manufacturer, freight forwarder, inspection partner, or other business partner',
      ],
    },
    {
      type: 'p',
      text: 'This Privacy Notice explains what personal data we process, why we process it, how long we keep it, who we may share it with, and what rights you have regarding your personal data.',
    },
  ],
  sections: [
    {
      title: '1. Who Is Responsible for Your Personal Data?',
      blocks: [
        { type: 'p', text: 'The data controller responsible for your personal data is:' },
        { type: 'ul', items: CONTACT_ITEMS_EN },
        {
          type: 'p',
          text: 'For the purposes of this Privacy Notice, DeMaxtore refers to the brand and platform operated by Demakstore Gıda İhracat San. ve Tic. Ltd. Şti.',
        },
      ],
    },
    {
      title: '2. What Personal Data Do We Collect?',
      blocks: [
        { type: 'p', text: 'We may collect and process the following categories of personal data:' },
        ...dataSubsection('Identification Information', [
          'Full name',
          'Company representative name',
          'Profile image, if provided',
          'Identification or verification information, where legally required',
        ]),
        ...dataSubsection('Business Contact Information', [
          'Business email address',
          'Phone number',
          'Job title',
          'Department',
          'Company name',
          'Company website',
          'Country and city',
          'Business address, where provided',
        ]),
        ...dataSubsection('Company and Supplier Information', [
          'Company profile information',
          'Product categories',
          'Product specifications',
          'Manufacturing capabilities',
          'Certifications',
          'Supplier verification details',
          'Buyer or supplier role information',
        ]),
        ...dataSubsection('Sourcing and Bidding Information', [
          'Product sourcing requests',
          'CommodityBid requests',
          'Quantity, packaging, target price, and delivery requirements',
          'Supplier offers',
          'Buyer and supplier communications',
          'Bid comparison details',
        ]),
        ...dataSubsection('Logistics and Shipment Information', [
          'Freight request details',
          'Pickup and delivery information',
          'Cargo type and shipment details',
          'FreightIQ comparison data',
          'Forwarder offers',
          'Shipment tracking updates',
          'Door-to-door logistics information',
        ]),
        ...dataSubsection('Inspection Information', [
          'Inspection request details',
          'Factory or supplier location',
          'Product quality requirements',
          'Inspection reports',
          'Photos, documents, and related communication',
        ]),
        ...dataSubsection('Financial and Transaction Information', [
          'Invoice information',
          'Payment-related business records',
          'Bank details, where required for payment or contractual purposes',
          'Accounting and tax-related information',
        ]),
        ...dataSubsection('Compliance Information', [
          'Know Your Customer / Know Your Supplier checks',
          'Anti-fraud checks',
          'Trade compliance',
          'Anti-money laundering obligations',
          'Anti-bribery and corruption compliance',
          'Sanctions or restricted-party screening',
        ]),
        ...dataSubsection('Technical and Usage Data', [
          'IP address',
          'Browser type and version',
          'Device type',
          'Operating system',
          'Time zone and location settings',
          'Website usage data',
          'Pages viewed',
          'Login data',
          'Cookie and consent preferences',
          'Interaction with website features',
        ]),
        ...dataSubsection('Marketing and Communication Data', [
          'Marketing preferences',
          'Communication preferences',
          'Email, WhatsApp, SMS, phone, or form communication records',
          'Campaign interaction data',
        ]),
        {
          type: 'p',
          text: 'If certain information is mandatory for a service, we may not be able to provide that service if the required information is not provided.',
        },
      ],
    },
    {
      title: '3. Why Do We Use Your Personal Data?',
      blocks: [
        { type: 'p', text: 'We process personal data for the purposes listed below:' },
        {
          type: 'ul',
          items: [
            'To provide access to DeMaxtore’s website and platform services',
            'To create and manage buyer, supplier, and partner accounts',
            'To process sourcing requests, RFQs, and CommodityBid processes',
            'To compare freight offers and support FreightIQ services',
            'To coordinate inspection and live shipment tracking services',
            'To communicate with users, buyers, suppliers, and logistics partners',
            'To verify company, supplier, and partner information',
            'To provide customer support and manage business relationships',
            'To improve our website, services, search, and user experience',
            'To personalize relevant supplier, product, and freight recommendations where permitted',
            'To send service-related, transactional, legal, and marketing communications where allowed',
            'To prevent fraud, misuse, spam, and unauthorized activity',
            'To meet legal, tax, accounting, regulatory, and compliance obligations',
            'To protect the rights, safety, reputation, and legitimate interests of DeMaxtore',
          ],
        },
      ],
    },
    {
      title: '4. Personalized Services and Profiling',
      blocks: [
        {
          type: 'p',
          text: 'DeMaxtore may use certain data to improve the relevance of search results, supplier recommendations, freight comparisons, or sourcing suggestions.',
        },
        { type: 'p', text: 'For example, we may analyze:' },
        {
          type: 'ul',
          items: [
            'Search queries',
            'Product interests',
            'Sourcing history',
            'Requested categories',
            'Country or market preferences',
            'Buyer or supplier behavior',
            'Platform usage patterns',
          ],
        },
        {
          type: 'p',
          text: 'This may help us provide more relevant supplier suggestions, product categories, freight options, or service recommendations.',
        },
        {
          type: 'p',
          text: 'Where this processing requires consent under applicable law, we will request your consent before using such data.',
        },
        {
          type: 'p',
          text: 'We do not use automated decision-making that produces legal or similarly significant effects on users without human involvement.',
        },
      ],
    },
    {
      title: '5. Marketing Communications',
      blocks: [
        {
          type: 'p',
          text: 'We may send marketing communications about DeMaxtore services, including supplier sourcing, CommodityBid, FreightIQ, inspection services, shipment tracking, and other B2B import process management solutions.',
        },
        { type: 'p', text: 'These communications may be sent by:' },
        {
          type: 'ul',
          items: ['Email', 'WhatsApp', 'SMS', 'Phone', 'Other electronic communication channels'],
        },
        {
          type: 'p',
          text: 'You can unsubscribe from marketing communications at any time by using the unsubscribe option where available or by contacting us at:',
        },
        { type: 'p', text: 'support@demaxtore.com' },
        {
          type: 'p',
          text: 'Even if you opt out of marketing communications, we may still send you important service, legal, security, or transaction-related messages.',
        },
      ],
    },
    {
      title: '6. Third-Party Links',
      blocks: [
        {
          type: 'p',
          text: 'Our website may include links to third-party websites, tools, plug-ins, applications, suppliers, manufacturers, logistics providers, inspection partners, or external platforms.',
        },
        {
          type: 'p',
          text: 'Clicking on third-party links or enabling third-party integrations may allow those third parties to collect or process data about you.',
        },
        {
          type: 'p',
          text: 'DeMaxtore does not control third-party websites and is not responsible for their privacy practices. We recommend that you review the privacy notices and cookie policies of any third-party websites you visit.',
        },
      ],
    },
    {
      title: '7. Cookies and Consent Management',
      blocks: [
        { type: 'p', text: 'We use cookies and similar technologies on www.demaxtore.com to:' },
        {
          type: 'ul',
          items: [
            'Ensure the website functions properly',
            'Remember your cookie preferences',
            'Improve website performance',
            'Analyze website traffic',
            'Enhance user experience',
            'Support marketing and advertising activities',
          ],
        },
        {
          type: 'p',
          text: 'Where required by law, we ask for your consent before placing non-essential cookies on your device.',
        },
        {
          type: 'p',
          text: 'You may accept, reject, or manage your cookie preferences through our cookie banner or cookie preference tool, where available.',
        },
        {
          type: 'p',
          text: 'For more details, please refer to our separate Cookie Policy.',
        },
      ],
    },
    {
      title: '8. Who Do We Share Your Personal Data With?',
      blocks: [
        { type: 'p', text: 'We may share your personal data with:' },
        {
          type: 'ul',
          items: [
            'Buyers',
            'Suppliers',
            'Manufacturers',
            'Freight forwarders',
            'Logistics providers',
            'Inspection service providers',
            'Customs, documentation, or trade service partners',
            'IT, hosting, CRM, analytics, email, and cloud service providers',
            'Marketing and communication service providers',
            'Legal, accounting, audit, and compliance advisors',
            'Banks or payment service providers, where necessary',
            'Public authorities, courts, regulators, or law enforcement bodies where legally required',
          ],
        },
        {
          type: 'p',
          text: 'We only share personal data where necessary for the purposes described in this Privacy Notice or where required by law.',
        },
        { type: 'p', text: 'We do not sell personal data.' },
      ],
    },
    {
      title: '9. International Data Transfers',
      blocks: [
        {
          type: 'p',
          text: 'Because DeMaxtore operates in international trade and B2B import processes, your personal data may be transferred to or accessed from countries outside your country of residence.',
        },
        {
          type: 'p',
          text: 'Where required by applicable data protection laws, we use appropriate safeguards to protect personal data during international transfers. These safeguards may include contractual protections, technical measures, organizational measures, or other lawful transfer mechanisms.',
        },
      ],
    },
    {
      title: '10. How Long Do We Keep Your Personal Data?',
      blocks: [
        {
          type: 'p',
          text: 'We keep personal data only for as long as necessary for the purposes described in this Privacy Notice.',
        },
        { type: 'p', text: 'Retention periods may depend on:' },
        {
          type: 'ul',
          items: [
            'The type of data',
            'The purpose of processing',
            'The duration of the business relationship',
            'Legal, tax, accounting, or regulatory obligations',
            'Dispute resolution requirements',
            'Fraud prevention and security needs',
          ],
        },
        {
          type: 'p',
          text: 'When personal data is no longer required, we delete, anonymize, or securely archive it.',
        },
      ],
    },
    {
      title: '11. How Do We Protect Your Personal Data?',
      blocks: [
        { type: 'p', text: 'We use reasonable technical and organizational measures to protect personal data against:' },
        {
          type: 'ul',
          items: [
            'Unauthorized access',
            'Loss',
            'Misuse',
            'Alteration',
            'Disclosure',
            'Destruction',
            'Fraudulent activity',
            'Security incidents',
          ],
        },
        {
          type: 'p',
          text: 'However, no internet-based service can be guaranteed to be completely secure. Users are responsible for keeping their account credentials confidential and for notifying us immediately if they suspect unauthorized access.',
        },
      ],
    },
    {
      title: '12. Your Rights',
      blocks: [
        { type: 'p', text: 'Depending on applicable data protection laws, you may have the right to:' },
        {
          type: 'ul',
          items: [
            'Request access to your personal data',
            'Request correction of inaccurate or incomplete data',
            'Request deletion of your personal data',
            'Object to certain processing activities',
            'Request restriction of processing',
            'Withdraw consent where processing is based on consent',
            'Request data portability',
            'Lodge a complaint with a competent data protection authority',
          ],
        },
        { type: 'p', text: 'To exercise your rights, please contact us at:' },
        { type: 'p', text: 'support@demaxtore.com' },
        { type: 'p', text: 'We may need to verify your identity before responding to your request.' },
      ],
    },
    {
      title: '13. WhatsApp and Other Communication Channels',
      blocks: [
        {
          type: 'p',
          text: 'If you contact us through WhatsApp, email, SMS, phone, or other communication channels, we may process and store the communication content and related contact information.',
        },
        { type: 'p', text: 'This helps us:' },
        {
          type: 'ul',
          items: [
            'Respond to your request',
            'Provide support',
            'Manage buyer, supplier, and partner relationships',
            'Coordinate sourcing, freight, inspection, or shipment services',
            'Maintain business records',
          ],
        },
        {
          type: 'p',
          text: 'Third-party communication tools may also process your data according to their own privacy policies.',
        },
      ],
    },
    {
      title: '14. Business Transactions',
      blocks: [
        {
          type: 'p',
          text: 'If DeMaxtore is involved in a merger, acquisition, investment process, restructuring, asset sale, or similar business transaction, personal data may be transferred or disclosed as part of that transaction, where legally permitted.',
        },
        {
          type: 'p',
          text: 'In such cases, we will take appropriate measures to protect personal data.',
        },
      ],
    },
    {
      title: '15. Children’s Data',
      blocks: [
        {
          type: 'p',
          text: 'DeMaxtore provides B2B services and does not target children.',
        },
        {
          type: 'p',
          text: 'We do not knowingly collect personal data from individuals under the age of 18. If we become aware that such data has been collected, we will take reasonable steps to delete it.',
        },
      ],
    },
    {
      title: '16. Updates to This Website Privacy Notice',
      blocks: [
        {
          type: 'p',
          text: 'We may update this Website Privacy Notice from time to time to reflect changes in our services, legal requirements, business operations, or data processing practices.',
        },
        {
          type: 'p',
          text: 'The latest version will be published on www.demaxtore.com with the updated date.',
        },
      ],
    },
    {
      title: '17. Contact Us',
      blocks: [
        {
          type: 'p',
          text: 'For any questions, requests, or concerns about this Website Privacy Notice or how we process personal data, please contact us:',
        },
        { type: 'ul', items: CONTACT_ITEMS_EN },
      ],
    },
  ],
};

export const WEBSITE_PRIVACY_NOTICE_BY_LANG = {
  en: noticeEn,
  fr: noticeFr,
  es: noticeEs,
  ar: noticeAr,
};
